import { Edge, Node } from "reactflow"
import JSZip from "jszip"
import { GraphNodeType } from "@custom-types/graphs"
import { XYPosition } from "@xyflow/system"
import { LAG_LABEL_REGEX } from "@utils/node/labelValidation"

import {
  BaseGraphCoordinates,
  BaseGraphMetaZipGroup,
  BaseGraphMetaZipNode,
  BaseGraphZipNode,
  GraphNodeData,
  GraphNodeEdgeData,
  GroupNodeData,
  UnpackedGraph,
} from "@store/services/converter/types"
import { getTableRowsFromCsvContent } from "@utils/file"

export const NODE_DEFAULT_WIDTH = 90
export const NODE_DEFAULT_HEIGHT = 30
export const GROUP_MARGIN_WIDTH = 10
export const GROUP_MARGIN_HEIGHT = 30
export const GROUP_TEXT_HEIGHT_PADDING = 10

const groupSizeDiv = document.createElement("div")
groupSizeDiv.style.position = "absolute"
groupSizeDiv.style.top = "0"
groupSizeDiv.style.visibility = "hidden"
// groupSizeDiv.style.wordBreak = "break-all"
groupSizeDiv.style.fontSize = "16px"
groupSizeDiv.style.minWidth = `${NODE_DEFAULT_WIDTH}px`
groupSizeDiv.style.maxWidth = "180px"
document.body.appendChild(groupSizeDiv)

export class GraphConverter {
  static async packZip(data: Partial<UnpackedGraph>): Promise<Blob> {
    const zip = new JSZip()
    const fileName = data.meta?.fileName || "graph"
    zip.file(`${fileName}.json`, JSON.stringify(data.graph))
    zip.file(`${fileName}.meta`, JSON.stringify(data.meta))
    zip.file(`${fileName}.xy`, JSON.stringify(data.xy))
    if (data.scenario) zip.file(".scenario", data.scenario)
    return zip.generateAsync({ type: "blob" })
  }

  static convertToZipStructure(data: GraphNodeEdgeData): UnpackedGraph {
    const result: UnpackedGraph = {
      graph: { nodes: [] },
      meta: {
        nodes: {},
        groups: {},
        ...data.meta,
      },
      xy: { coordinates: {} },
      scenario: data.scenario,
    }

    Object.values(data.nodeMap).forEach((item) => {
      if (item.table) {
        if (!result.graph.attributes) result.graph.attributes = []
        result.graph.attributes.push({
          name: item.node.name,
          type: item.node.type,
          properties: item.table,
        })
      } else {
        result.graph.nodes.push(item.node)
      }
      result.meta.nodes[item.node.name] = item.display
      result.xy.coordinates[item.node.name] = item.position
    })
    Object.values(data.groupMap).forEach((item) => {
      result.xy.coordinates[item.id] = item.position
      const group = structuredClone(item.group)
      // ms: we always need expanded state when saving otherwise tkrisk will set {x: 0, y: 0} and break reimporting
      // might be that it can be fixed by calculating correct x,y positions during expand,
      // but it's the only way to fix it right now
      group.isCollapsed = false
      result.meta.groups[item.id] = group
    })
    return result
  }

  static convertToReactFlow({
    graph,
    meta,
    xy,
    scenario: embeddedScenario,
  }: Partial<UnpackedGraph>): GraphNodeEdgeData {
    if (!graph || !meta || !xy) {
      return {
        nodes: [],
        edges: [],
        labelMap: {},
        nodeMap: {},
        groupMap: {},
        childrenGroupMap: {},
        positionMap: {},
        meta: undefined,
        zIndex: 1,
      }
    }

    const mnemonicMap = new Map<string, Record<string, string>[]>()
    if (embeddedScenario) {
      const tableRows = getTableRowsFromCsvContent(embeddedScenario)
      tableRows.forEach((row) => {
        if (!("Mnem" in row)) return
        const mnem = row["Mnem"]
        if (!mnemonicMap.has(mnem)) {
          mnemonicMap.set(mnem, [])
        }
        const mnemData = mnemonicMap.get(mnem)
        if (!mnemData) return
        mnemData.push(row)
      })
    }

    const edges: Edge[] = []
    const labelMap: { [key: string]: string } = {}
    const nodeMap: { [key: string]: GraphNodeData } = {}
    const positionMap: { [key: string]: XYPosition } = {}
    const childrenGroupMap = {}
    const { coordinates } = xy
    const { nodes: metaNodes, groups, ...exportMeta } = meta
    const {
      nodes,
      zIndex: startZIndex,
      groupMap,
    } = this.convertGroups(groups, childrenGroupMap, coordinates, labelMap, positionMap)
    let zIndex = startZIndex
    // tables
    if (graph.attributes) {
      for (const attr of graph.attributes) {
        // tkrisk R724 format introduced lag nodes and multistep process
        // if user adds lag node and then removes it - tkrisk will add this key to the file
        // we don't want to display it that's why we skip it
        if (attr.name === "__multistep_set_values__") {
          continue
        }
        if (attr.type !== GraphNodeType.TABLE) {
          throw new Error(`Unsupported attribute type ${attr.type}`)
        }
        const table = attr.properties
        const node: BaseGraphZipNode = {
          name: attr.name,
          type: attr.type,
          properties: { df_cpd: {} },
        }
        const display = metaNodes[attr.name]
        const position = { ...coordinates[attr.name] }
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        positionMap[attr.name] = position
        this.addNode(
          attr.name,
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          { node, table, display, type: attr.type, position, labelMap, zIndex },
          nodes,
          labelMap,
          nodeMap,
        )
      }
      zIndex++
    }
    // nodes
    for (const node of graph.nodes) {
      const display = metaNodes[node.name]
      const position = { ...coordinates[node.name] }
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      positionMap[node.name] = position

      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      const type = this.getNodeType(node, display)
      const scenario = display ? this.getScenarioRecords(display, mnemonicMap) : []

      this.addNode(
        node.name,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        { node, display, type, position, labelMap, zIndex, scenario },
        nodes,
        labelMap,
        nodeMap,
      )
      zIndex++
    }

    // edges - TODO maybe unify with convertEdges
    let edgeIndex = zIndex
    const uniqueEdges = new Set<string>()
    for (const [id, node] of Object.entries(nodeMap)) {
      if (nodes.has(id)) {
        edgeIndex = this.convertNodeEdges(node, edges, uniqueEdges, nodes, nodeMap, edgeIndex)
      } else {
        edgeIndex = this.convertGroupEdges(node, nodes, nodeMap, edges, uniqueEdges, edgeIndex)
      }
    }

    return {
      nodes: Array.from(nodes.values()),
      edges,
      labelMap,
      nodeMap,
      groupMap,
      childrenGroupMap,
      positionMap,
      meta: exportMeta,
      zIndex,
      scenario: embeddedScenario,
    }
  }

  static getNodeType(node: BaseGraphZipNode, display: BaseGraphMetaZipNode): GraphNodeType {
    // pseudo node conversion
    if (
      // condition to allow 'a_sample_value' and expression based lag nodes
      LAG_LABEL_REGEX.test(display.label) &&
      !display["Forecast Mnem"] &&
      !display["Scenario Mnem"]
    ) {
      return GraphNodeType.LAG
    } else if (display["Forecast Mnem"]) {
      return GraphNodeType.FORECAST
    } else if (display["Scenario Mnem"]) {
      return GraphNodeType.SCENARIO
    }
    return node.type
  }

  private static getScenarioRecords = (
    display: BaseGraphMetaZipNode,
    mnemonicMap: Map<string, Record<string, string>[]>,
  ): Record<string, string>[] => {
    const name = display["Forecast Mnem"] || display["Scenario Mnem"] || ""
    return mnemonicMap.get(name) ?? []
  }

  private static addNode(
    nodeName: string,
    data: GraphNodeData,
    nodes: Map<string, Node<GroupNodeData | GraphNodeData>>,
    labelMap: { [key: string]: string },
    nodeMap: { [key: string]: GraphNodeData },
  ): boolean {
    let added = false
    if (!data.display.groups || data.display.groups.length == 0) {
      nodes.set(nodeName, this.createNodeFromNodeData(data))
      added = true
    }

    labelMap[nodeName] = data.display.label || nodeName
    nodeMap[nodeName] = data
    return added
  }

  static createNodeFromNodeData(data: GraphNodeData): Node<GraphNodeData> {
    return {
      id: data.node.name,
      type: data.type,
      data,
      position: {
        x: Math.round(data.position.x),
        y: Math.round(data.position.y),
      },
      style: { width: NODE_DEFAULT_WIDTH, height: NODE_DEFAULT_HEIGHT },
      zIndex: data.zIndex,
    }
  }

  static convertEdges(
    zIndex: number,
    nodeMap: { [key: string]: GraphNodeData },
    nodeInternals: Map<string, Node<GroupNodeData | GraphNodeData>>,
  ): Edge[] {
    // edges - TODO collapsed group edges with visible nodes
    const uniqueEdges = new Set<string>()
    let edgeIndex = zIndex
    const edges: Edge[] = []
    for (const [id, node] of Object.entries(nodeMap)) {
      if (nodeInternals.has(id)) {
        edgeIndex = this.convertNodeEdges(
          node,
          edges,
          uniqueEdges,
          nodeInternals,
          nodeMap,
          edgeIndex,
        )
      } else {
        edgeIndex = this.convertGroupEdges(
          node,
          nodeInternals,
          nodeMap,
          edges,
          uniqueEdges,
          edgeIndex,
        )
      }
    }
    return edges
  }

  static getGroupTextSize = (text: string) => {
    groupSizeDiv.innerText = text
    return { width: groupSizeDiv.clientWidth, height: groupSizeDiv.clientHeight }
  }

  private static convertGroupEdges(
    node: GraphNodeData,
    nodes: Map<string, Node<GroupNodeData | GraphNodeData>>,
    nodeMap: { [key: string]: GraphNodeData },
    edges: Edge[],
    uniqueEdges: Set<string>,
    zIndex: number,
  ): number {
    if (!node.display.groups) {
      return zIndex
    }
    for (const groupId of node.display.groups) {
      if (!nodes.has(groupId)) {
        continue
      }
      // skip expanded groups
      if (!(nodes.get(groupId)?.data as GroupNodeData).group.isCollapsed) {
        continue
      }
      const attributes_lst = node.node.properties.attributes_lst || []
      // children
      for (const edge of attributes_lst) {
        // edge of node inside group is visible on graph, so we add groupId with edge
        if (nodes.has(edge)) {
          const id = `${edge}-${groupId}`
          if (uniqueEdges.has(id)) {
            continue
          }
          edges.push({
            id,
            type: "custom_base",
            source: groupId,
            target: edge,
            sourceHandle: groupId,
            animated: false,
            zIndex,
          })
          uniqueEdges.add(id)
          zIndex++
        } else {
          // both edge and node inside group are not visible, so we want to visit all groups inside edge
          // to see if group is visible
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          const groups = nodeMap[edge].display.groups
          if (!groups) {
            continue
          }
          // skip same groups
          if (groups.includes(groupId)) {
            continue
          }
          for (const groupEdgeId of groups) {
            // group is not visible
            if (!nodes.has(groupEdgeId)) {
              continue
            }
            // group is not collapsed
            if (!(nodes.get(groupEdgeId)?.data as GroupNodeData).group.isCollapsed) {
              continue
            }
            const id = `${groupEdgeId}-${groupId}`
            if (uniqueEdges.has(id)) {
              continue
            }
            edges.push({
              id,
              type: "custom_base",
              source: groupId,
              target: groupEdgeId,
              sourceHandle: groupId,
              animated: false,
              zIndex,
            })
            uniqueEdges.add(id)
            zIndex++
          }
        }
      }
      // parents
      const parents_lst = node.node.properties.parents_lst || []
      for (const edge of parents_lst) {
        // edge of node inside group is visible on graph, so we add groupId with edge
        if (nodes.has(edge)) {
          const id = `${groupId}-${edge}`
          if (uniqueEdges.has(id)) {
            continue
          }
          edges.push({
            id,
            type: "custom_base",
            source: edge,
            target: groupId,
            sourceHandle: edge,
            animated: false,
            zIndex,
          })
          uniqueEdges.add(id)
          zIndex++
        } else {
          // both edge and node inside group are not visible, so we want to visit all groups inside edge
          // to see if group is visible
          // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
          const groups = nodeMap[edge].display.groups
          if (!groups) {
            continue
          }
          // skip same groups
          if (groups.includes(groupId)) {
            continue
          }
          for (const groupEdgeId of groups) {
            // group is not visible
            if (!nodes.has(groupEdgeId)) {
              continue
            }
            // group is not collapsed
            if (!(nodes.get(groupEdgeId)?.data as GroupNodeData).group.isCollapsed) {
              continue
            }
            const id = `${groupId}-${groupEdgeId}`
            if (uniqueEdges.has(id)) {
              continue
            }
            edges.push({
              id,
              type: "custom_base",
              source: groupEdgeId,
              target: groupId,
              sourceHandle: groupEdgeId,
              animated: false,
              zIndex,
            })
            uniqueEdges.add(id)
            zIndex++
          }
        }
      }
    }
    return zIndex
  }

  private static convertNodeGroupEdge(
    nodeId: string,
    edgeId: string,
    isTarget: boolean,
    edges: Edge[],
    uniqueEdges: Set<string>,
    nodes: Map<string, Node<GroupNodeData | GraphNodeData>>,
    nodeMap: { [key: string]: GraphNodeData },
    zIndex: number,
  ): number {
    const node = nodeMap[edgeId]
    if (!node?.display.groups) return zIndex
    for (const groupId of node.display.groups) {
      // node is not visible
      if (!nodes.has(groupId)) {
        continue
      }
      // group is not collapsed
      if (!(nodes.get(groupId)?.data as GroupNodeData).group.isCollapsed) {
        continue
      }
      if (isTarget) {
        const id = `${groupId}-${nodeId}`
        if (uniqueEdges.has(id)) {
          continue
        }
        edges.push({
          id,
          type: "custom_base",
          source: nodeId,
          target: groupId,
          sourceHandle: nodeId,
          animated: false,

          zIndex,
        })
        uniqueEdges.add(id)
        zIndex++
      } else {
        const id = `${nodeId}-${groupId}`
        if (uniqueEdges.has(id)) {
          continue
        }
        edges.push({
          id,
          type: "custom_base",
          source: groupId,
          target: nodeId,
          sourceHandle: groupId,
          animated: false,

          zIndex,
        })
        uniqueEdges.add(id)
        zIndex++
      }
    }
    return zIndex
  }

  private static convertNodeEdges(
    node: GraphNodeData,
    edges: Edge[],
    uniqueEdges: Set<string>,
    nodes: Map<string, Node<GroupNodeData | GraphNodeData>>,
    nodeMap: { [key: string]: GraphNodeData },
    zIndex: number,
  ): number {
    const dataNode = node.node
    const attributes_lst = dataNode.properties.attributes_lst || []
    // children TODO tr: tables?
    for (const edge of attributes_lst) {
      if (!nodes.has(edge)) {
        zIndex = this.convertNodeGroupEdge(
          dataNode.name,
          edge,
          true,
          edges,
          uniqueEdges,
          nodes,
          nodeMap,
          zIndex,
        )
        continue
      }
      const id = `${edge}-${dataNode.name}`
      if (uniqueEdges.has(id)) continue
      edges.push({
        id,
        type: "custom_base_other",
        source: edge,
        target: dataNode.name,
        sourceHandle: edge,
        animated: false,

        zIndex,
      })
      uniqueEdges.add(id)
      zIndex++
    }
    // parents
    const parents_lst = dataNode.properties.parents_lst || []
    for (const edge of parents_lst) {
      if (!nodes.has(edge)) {
        zIndex = this.convertNodeGroupEdge(
          dataNode.name,
          edge,
          false,
          edges,
          uniqueEdges,
          nodes,
          nodeMap,
          zIndex,
        )
        continue
      }
      const id = `${dataNode.name}-${edge}`
      if (uniqueEdges.has(id)) continue
      edges.push({
        id,
        type: "custom_base",
        source: edge,
        target: dataNode.name,
        sourceHandle: edge,
        animated: false,

        zIndex,
      })
      uniqueEdges.add(id)
      zIndex++
    }
    return zIndex
  }

  private static convertGroups(
    groups: { [key: string]: BaseGraphMetaZipGroup },
    childrenGroupMap: { [key: string]: string[] },
    coordinates: BaseGraphCoordinates,
    labelMap: { [key: string]: string },
    positionMap: { [key: string]: XYPosition },
  ): {
    nodes: Map<string, Node<GroupNodeData | GraphNodeData>>
    zIndex: number
    groupMap: { [key: string]: GroupNodeData }
  } {
    const nodes = new Map<string, Node<GroupNodeData | GraphNodeData>>()
    const groupMap: { [key: string]: GroupNodeData } = {}
    let zIndex = 1
    if (Object.keys(groups).length == 0) return { nodes, zIndex, groupMap }

    const sortedGroups = this.sortGroupsZIndex(groups)

    for (const { key: id, group } of sortedGroups) {
      if (group.parent) {
        if (!childrenGroupMap[group.parent]) childrenGroupMap[group.parent] = []
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        childrenGroupMap[group.parent].push(id)
      }
      labelMap[id] = group.label
      const position = { ...coordinates[id] }
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      positionMap[id] = position

      const data: GroupNodeData = {
        id,
        group,
        labelMap,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        position,
        zIndex,
      }
      // All groups are collapsed by default
      group.isCollapsed = true

      let color = group.color
      //fix colors without # symbol
      if (!color.startsWith("#")) {
        color = `#${color}`
      }
      if (!group.parent) {
        const size = this.getGroupTextSize(`${data.group.label}`)
        nodes.set(id, {
          id,
          type: "group",
          style: {
            backgroundColor: color,
            color: "#000",
            width: size.width,
            height: size.height + GROUP_TEXT_HEIGHT_PADDING,
          },
          data,
          className: "light",
          position: {
            x: Math.round(data.position.x),
            y: Math.round(data.position.y),
          },
          zIndex,
        })
      }
      zIndex++
      groupMap[id] = data
    }
    return { nodes, zIndex, groupMap }
  }

  private static sortGroupsZIndex(groups: {
    [key: string]: BaseGraphMetaZipGroup
  }): { key: string; group: BaseGraphMetaZipGroup }[] {
    // first create map of all group keys with children parent<children[]> map
    const obj: { [key: string]: { key: string; group: BaseGraphMetaZipGroup }[] } = {}
    // eslint-disable-next-line guard-for-in -- FIX: check for unwanted properties or refactor to for..of
    for (const key in groups) {
      const group = groups[key]
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      if (!group.parent) {
        if (!obj["undefined"]) obj["undefined"] = []
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        obj["undefined"].push({ key, group })
      } else {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        if (!obj[group.parent]) obj[group.parent] = []
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        obj[group.parent].push({ key, group })
      }
    }
    // create sorted groups array by getting all groups without parents (undefined)
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    const sortedGroups: { key: string; group: BaseGraphMetaZipGroup }[] = obj["undefined"].concat()
    // temporary array and initial size of array
    // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
    let temp: { key: string; group: BaseGraphMetaZipGroup }[] = obj["undefined"]
    let tempSize = temp.length
    // remove undefined object
    delete obj["undefined"]
    // for each item in temp we get all children of parent<children[]> and add it to temp
    // we also add all children to sortedGroups and remove group from obj map
    // after that we splice array to remove all children that we already iterated over
    // we repeat it until no children are left in obj map (temp array is empty - cheaper call)
    while (temp.length > 0) {
      for (let i = 0; i < tempSize; i++) {
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        const group = obj[temp[i].key]
        if (!group) continue
        temp.push(...group)
        sortedGroups.push(...group)
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        delete obj[temp[i].key]
      }
      temp = temp.slice(tempSize)
      tempSize = temp.length
    }
    return sortedGroups
  }
}
